/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-39 §3 (lee Q2, 23/10) — tab "Độ sáng đèn": 32 TIMER DIM của SLCU. SLCU tự chạy lịch (kể cả seek khi khởi động); trang
 * này chỉ CÀI và XEM. Thứ tự: dòng "Bây giờ" · chip ngày + đồ thị bậc thang (kéo điểm: giờ bước 5 phút, mức bước 5 %) · bảng
 * 32 dòng (dòng không dùng thu gọn) · khối "Áp cho" + Gửi.
 *   · TK-45 §4 (lee 25/10): đồ thị MỖI NHÓM MỘT ĐƯỜNG, trục x = giờ đồng hồ từ (On − 4 h) tới (Off + 4 h) qua nửa đêm; chỉ vẽ
 *     giữa On và Off của lịch đóng cắt TỦ ĐANG CHẠY cho ngày đang chọn; từ On tới timer đầu tiên của nhóm = 100 %
 *     (`lib/doThiDim.ts`, thuần). Chưa có lịch đóng cắt ⇒ "Chưa có lịch đóng cắt", không vẽ đường.
 *   · TK-45 §3: cột Nhóm là ô NHẬP SỐ 0–254 (sai / trống ⇒ viền `nguy`, Gửi khoá); bỏ "Dùng" ⇒ nhóm 255 như cũ.
 *   · "Bây giờ" tính trên bản TỦ ĐANG CHẠY (lich_dim ACK_OK gần nhất) — đó là cái đèn đang làm.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as PE } from 'react';
import { Plus } from 'lucide-react';
import { Badge, Button, ErrorBox, Spinner, useToast } from '../ui';
import { useAuth } from '../../lib/auth';
import { errorMessage } from '../../lib/api';
import { useSchedule, useTree } from '../../lib/queries';
import { NHAN_TRANG_THAI_LENH, useNemaTu } from '../../lib/nemaApi';
import { ngayVN, type NgoLich } from '../../lib/gioCapDien';
import { coThu, gioChu, lucVN, NHOM_KHONG_DUNG, seekDim, type TimerDim } from '../../lib/seekDim';
import { useGuiTimerDim, useTimerDim, type KetApDimTu, type NhomTimer } from '../../lib/caiDat';
import { boSo0, docNhom, doThiDim, gioDongHo, mauNhomSo, nhomDangDung, onOffDem, type KetOnOff } from '../../lib/doThiDim';
import { KhoiApCho, type ApCho } from './KhoiApCho';

const THU = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
/** Chú giải: "Nhóm 0 (tên nếu có)". Màu: bảng phân loại `MAU_NHOM` theo số nhóm — không màu trạng thái. */
const tenNhomChuGiai = (g: NhomTimer | undefined, so: number) => `Nhóm ${so}${g?.name ? ` (${g.name})` : ''}`;
const BUOC = 5;

/* ───────── đồ thị ───────── */
const DEM = { l: 34, r: 10, t: 22, b: 22 };
const CAO = 240;

/** Bề rộng thật của hộp (đo bằng ResizeObserver — không suy từ cửa sổ). */
function useBeRong<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el); setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

function DoThi({ ds, thu, nhomMap, oo, bayGio, sua, doiTimer }: {
  ds: TimerDim[]; thu: number; nhomMap: Map<number, NhomTimer>; oo: KetOnOff; bayGio: number | null;
  sua: boolean; doiTimer: (so: number, p: Partial<TimerDim>) => void;
}) {
  const [hop, w] = useBeRong<HTMLDivElement>();
  const [keo, setKeo] = useState<number | null>(null);
  /* mỗi nhóm trong khoảng của NGÕ của nhánh đèn trong nhóm (nhóm chưa gán nhánh ⇒ mọi ngõ) */
  const kq = useMemo(() => doThiDim(ds, thu, oo, (so) => nhomMap.get(so)?.nhanh ?? []), [ds, thu, oo, nhomMap]);
  if (oo.loai !== 'co') {
    /* luật số 0: tủ CHƯA BÁO lịch ⇒ "chưa biết" (màu chưa rõ), không vẽ như "không có điện"; tủ có lịch mà đêm ấy không ngõ nào bật ⇒ "Chưa có…" */
    return (
      <div ref={hop} className="w-full" data-o="do-thi-dim">
        {oo.loai === 'chua-biet'
          ? <p className="m-0 py-14 text-center text-sm text-chua-ro" data-o="lich-tu-chua-biet">Lịch đóng cắt: chưa biết</p>
          : <p className="m-0 py-14 text-center text-sm text-chu-3" data-o="chua-co-lich">Chưa có lịch đóng cắt</p>}
      </div>
    );
  }
  const [a, b] = oo.truc;
  const rong = Math.max(0, w - DEM.l - DEM.r), cao = CAO - DEM.t - DEM.b;
  const x = (p: number) => DEM.l + ((p - a) / (b - a)) * rong;
  const y = (m: number) => DEM.t + ((100 - m) / 100) * cao;
  /* nhãn giờ: mỗi 1 / 2 / 3 h tuỳ bề rộng (nhãn không chồng nhau) */
  const pxGio = rong / ((b - a) / 60);
  const buocGio = pxGio >= 38 ? 1 : pxGio >= 19 ? 2 : 3;
  const vach: number[] = [];
  for (let p = Math.ceil(a / 60) * 60; p <= b; p += 60 * buocGio) vach.push(p);
  const tuChuot = (e: PE<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const p = a + ((e.clientX - r.left - DEM.l) / rong) * (b - a);
    const m = Math.round((((DEM.t + cao - (e.clientY - r.top)) / cao) * 100) / 5) * 5;
    return { gio_phut: ((Math.round(p / BUOC) * BUOC) % 1440 + 1440) % 1440, muc: Math.min(100, Math.max(0, m)) };
  };
  const nhanDung = (p: number, chu: string, lop: string, k: string, mo = false) => (
    <g key={k} data-vach={chu}>
      <line x1={x(p)} x2={x(p)} y1={DEM.t - 4} y2={DEM.t + cao} className={lop} strokeDasharray={mo ? '3 4' : undefined} />
      <text x={x(p)} y={DEM.t - 8} textAnchor="middle" className="fill-chu-2 text-[11px]">{chu}</text>
    </g>
  );
  return (
    <div ref={hop} className="w-full" data-o="do-thi-dim" data-truc={`${a}-${b}`}>
      {w > 0 && (
        <svg width={w} height={CAO} className="block touch-none select-none"
          onPointerMove={(e) => { if (keo !== null) doiTimer(keo, tuChuot(e)); }}
          onPointerUp={() => setKeo(null)} onPointerLeave={() => setKeo(null)}>
          {[0, 50, 100].map((m) => <g key={m}><line x1={DEM.l} x2={DEM.l + rong} y1={y(m)} y2={y(m)} className="stroke-vien" strokeDasharray={m === 0 ? undefined : '3 4'} /><text x={DEM.l - 6} y={y(m) + 4} textAnchor="end" className="fill-chu-3 text-[11px]">{m}%</text></g>)}
          {vach.map((p) => <text key={p} x={x(p)} y={CAO - 6} textAnchor={p === a ? 'start' : p === b ? 'end' : 'middle'} className="fill-chu-3 text-[11px]">{gioDongHo(p)}</text>)}
          {1440 > a && 1440 < b && nhanDung(1440, '00:00', 'stroke-chu-3', 'nua-dem', true)}
          {oo.chung
            ? [nhanDung(oo.on, 'On', 'stroke-chu-2', 'on'), nhanDung(oo.off, 'Off', 'stroke-chu-2', 'off')]
            : oo.ngo.flatMap((g) => [nhanDung(g.on, `N${g.index}`, 'stroke-chu-3', `on${g.index}`), nhanDung(g.off, `N${g.index}`, 'stroke-chu-3', `off${g.index}`)])}
          {bayGio !== null && bayGio > a && bayGio < b && <line x1={x(bayGio)} x2={x(bayGio)} y1={DEM.t} y2={DEM.t + cao} className="stroke-nhan" strokeDasharray="4 3" data-o="vach-bay-gio" />}
          {kq.duong.map((d, i) => {
            /* hai nhóm trùng mức ⇒ đường sau lệch 1,5 px / nhóm, không che nhau */
            const dy = i * 1.5;
            const p = d.bac.map((c, k) => `${k === 0 ? `M${x(c.tu)},${y(c.muc) + dy}` : `V${y(c.muc) + dy}`}H${x(c.den)}`).join('');
            return <path key={d.nhom} d={p} fill="none" stroke={mauNhomSo(d.nhom)} strokeWidth={2.5} data-duong={d.nhom} data-bac={d.bac.map((c) => `${c.tu}-${c.den}:${c.muc}`).join(',')} />;
          })}
          {kq.duong.flatMap((d, i) => d.diem.map((e) => (
            <circle key={`${d.nhom}-${e.so}-${e.luc}`} cx={x(e.luc)} cy={y(e.muc) + i * 1.5} r={keo === e.so ? 8 : 6} fill={mauNhomSo(d.nhom)}
              className={`stroke-nen ${sua ? 'cursor-grab' : ''}`} strokeWidth={2} data-diem={e.so}
              onPointerDown={(ev) => { if (!sua) return; ev.currentTarget.ownerSVGElement?.setPointerCapture(ev.pointerId); setKeo(e.so); }}>
              <title>{`Timer ${e.so} · ${gioDongHo(e.luc)} · ${e.muc} % · ${tenNhomChuGiai(nhomMap.get(d.nhom), d.nhom)}`}</title>
            </circle>
          )))}
        </svg>
      )}
    </div>
  );
}

/* ───────── bảng 32 dòng ───────── */
/** TK-45 §3: ô Nhóm NHẬP SỐ 0–254 (bỏ ô thả xuống). Sai / trống ⇒ viền `nguy` + "0–254", báo lên tab để khoá Gửi; nhóm đã đặt
 *  tên trong CSDL ⇒ tên mờ cạnh ô. Bản nháp chữ giữ ở đây (chưa hợp lệ thì KHÔNG ghi vào timer). */
function ONhom({ t, nhom, sua, dung, doi, baoLoi }: {
  t: TimerDim; nhom: NhomTimer[]; sua: boolean; dung: boolean; doi: (p: Partial<TimerDim>) => void; baoLoi: (so: number, co: boolean) => void;
}) {
  const [chu, setChu] = useState(dung ? String(t.nhom) : '');
  useEffect(() => { setChu((c) => (dung && docNhom(c) === t.nhom ? c : dung ? String(t.nhom) : '')); }, [t.nhom, dung]);
  const loi = dung && docNhom(chu) === null;
  useEffect(() => { baoLoi(t.so, loi); }, [t.so, loi, baoLoi]);
  useEffect(() => () => baoLoi(t.so, false), [t.so, baoLoi]);
  const n = dung ? docNhom(chu) : null;
  const g = n === null ? undefined : nhom.find((x) => x.so === n);
  return (
    <span className="flex items-center gap-1.5">
      <input type="text" inputMode="numeric" maxLength={3} aria-label={`Nhóm timer ${t.so}`} aria-invalid={loi} disabled={!sua || !dung} value={chu} placeholder={dung ? '' : '—'}
        onChange={(e) => { const v = boSo0(e.target.value.replace(/\D/g, '')); setChu(v); const k = docNhom(v); if (k !== null) doi({ nhom: k }); }}
        className={`min-h-[34px] w-[4.5rem] rounded-lg border bg-nen-lom px-2 text-right text-sm tabular-nums text-chu-1 disabled:opacity-50 ${loi ? 'border-nguy' : 'border-vien'}`} data-o="o-nhom" />
      {loi && <span className="text-xs text-nguy" data-o="loi-nhom">0–254</span>}
      {!loi && g && <span className="max-w-[9rem] truncate text-xs text-chu-3" title={g.name} data-o="ten-nhom">{g.name}</span>}
    </span>
  );
}

function DongTimer({ t, nhom, sua, doi, baoLoi }: { t: TimerDim; nhom: NhomTimer[]; sua: boolean; doi: (p: Partial<TimerDim>) => void; baoLoi: (so: number, co: boolean) => void }) {
  const dung = t.nhom !== NHOM_KHONG_DUNG;
  return (
    <tr className="border-t border-vien" data-timer={t.so}>
      <td className="px-3 py-1.5 tabular-nums text-chu-2">{t.so}</td>
      <td className="px-2 py-1.5"><input type="checkbox" aria-label={`Dùng timer ${t.so}`} className="h-[18px] w-[18px] accent-nhan" checked={dung} disabled={!sua} onChange={(e) => doi({ nhom: e.target.checked ? 0 : NHOM_KHONG_DUNG })} /></td>
      <td className="px-2 py-1.5"><input type="time" step={300} aria-label={`Giờ timer ${t.so}`} disabled={!sua || !dung} value={gioChu(t.gio_phut)}
        onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); if (Number.isFinite(h) && Number.isFinite(m)) doi({ gio_phut: h * 60 + Math.round(m / BUOC) * BUOC }); }}
        className="min-h-[34px] rounded-lg border border-vien bg-nen-lom px-2 text-sm text-chu-1 disabled:opacity-50" /></td>
      {/* TK-45 §3: ô chữ số (không `type=number`) ⇒ giá trị luôn hiện đã chuẩn hoá — "050" ⇒ "50" */}
      <td className="px-2 py-1.5"><input type="text" inputMode="numeric" maxLength={3} aria-label={`Mức timer ${t.so}`} disabled={!sua || !dung} value={String(t.muc)}
        onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); doi({ muc: Math.min(100, Math.max(0, v === '' ? 0 : Number(v))) }); }}
        className="min-h-[34px] w-[4.5rem] rounded-lg border border-vien bg-nen-lom px-2 text-right text-sm tabular-nums text-chu-1 disabled:opacity-50" /></td>
      <td className="px-2 py-1.5">
        <span className="flex gap-1">
          {THU.map((n, i) => (
            <button key={n} type="button" disabled={!sua || !dung} aria-pressed={coThu(t.thu, i)} onClick={() => doi({ thu: t.thu ^ (1 << i) })}
              className={`h-7 min-w-[30px] rounded-md border px-1 text-xs font-semibold disabled:opacity-50 ${coThu(t.thu, i) ? 'border-nhan bg-nhan/15 text-chu-1' : 'border-vien text-chu-3'}`}>{n}</button>
          ))}
        </span>
      </td>
      <td className="px-2 py-1.5"><ONhom t={t} nhom={nhom} sua={sua} dung={dung} doi={doi} baoLoi={baoLoi} /></td>
    </tr>
  );
}

/* ───────── tab ───────── */
export function TabDoSang({ cabinetId, routeId, tenTu }: { cabinetId: string; routeId: string | null; tenTu: string }) {
  const q = useTimerDim(cabinetId);
  const nq = useNemaTu(cabinetId);
  const lichQ = useSchedule(cabinetId);
  const tree = useTree();
  const { coCoTuyen } = useAuth();
  const qd = coCoTuyen('canEditSchedule', routeId);
  const sua = qd === 'cho-qua';
  const { gui, apTuyen } = useGuiTimerDim(cabinetId);
  const toast = useToast();

  const [ds, setDs] = useState<TimerDim[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [thu, setThu] = useState(() => lucVN(new Date()).thu);
  const [hienDu, setHienDu] = useState(false);
  const [che, setChe] = useState<ApCho>('tu');
  const [ketTuyen, setKetTuyen] = useState<KetApDimTu[] | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);
  useEffect(() => { setDs(null); setDirty(false); setKetTuyen(null); setLoiNhom(new Set()); }, [cabinetId]);
  useEffect(() => { if (q.data && !dirty) setDs(q.data.nhap); }, [q.data, dirty]);

  const nhom = q.data?.nhom ?? [];
  const nhomMap = useMemo(() => new Map(nhom.map((g) => [g.so, g])), [nhom]);
  const nhomDung = useMemo(() => nhomDangDung(ds ?? []), [ds]);
  /* TK-45 §3: ô Nhóm đang sai (theo số timer) ⇒ khoá Gửi */
  const [loiNhom, setLoiNhom] = useState<ReadonlySet<number>>(new Set());
  const baoLoi = useCallback((so: number, co: boolean) => setLoiNhom((s) => { if (s.has(so) === co) return s; const n = new Set(s); if (co) n.add(so); else n.delete(so); return n; }), []);
  const homNay = lucVN(new Date(now));

  /* TK-45 §4: On / Off của đêm ngày đang chọn theo lịch đóng cắt TỦ ĐANG CHẠY (hoàng hôn / bình minh tính cho ngày ấy) */
  const lichTu: NgoLich[] | null = lichQ.data?.dangChay?.outputs ?? null;
  const matTroi = nq.data?.cabinet.matTroi ?? { sunsetMin: null, sunriseMin: null };
  const oo = useMemo<KetOnOff>(() => {
    const lech = (thu - homNay.thu + 7) % 7;
    return onOffDem(lichTu, ngayVN(now, lech), ngayVN(now, lech + 1), matTroi);
  }, [lichTu, thu, now, homNay.thu, matTroi]);
  /* vạch "bây giờ" theo phút tuyệt đối của trục: hôm nay ⇒ phút; đêm hôm qua còn chạy ⇒ phút + 24 h */
  const bayGioTruc = thu === homNay.thu ? homNay.phut : (thu + 1) % 7 === homNay.thu ? homNay.phut + 1440 : null;

  /* "Bây giờ" trên bản TỦ ĐANG CHẠY */
  const bayGio = useMemo(() => {
    const chay = q.data?.dangChay?.timers; if (!chay) return null;
    const dang = [...new Set(chay.filter((t) => t.nhom !== NHOM_KHONG_DUNG).map((t) => t.nhom))].sort((a, b) => a - b);
    return dang.map((so) => {
      const k = seekDim(chay, so, homNay);
      const g = nhomMap.get(so);
      const ten = `Nhóm ${so}${g ? ` ${g.name}` : ''}`;
      if (!k) return `${ten}: chưa có mức`;
      const luc = lucVN(new Date(now - k.phutTruoc * 60_000));
      return `${ten}: ${k.muc} % từ ${gioChu(luc.phut)} ${THU[luc.thu]}`;
    });
  }, [q.data, nhomMap, now, homNay]);

  if (q.isLoading || (!ds && !q.isError)) return <Spinner label="Đang tải lịch dim…" />;
  if (q.isError || !ds) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;

  const doiTimer = (so: number, p: Partial<TimerDim>) => { setDs((d) => d && d.map((t) => (t.so === so ? { ...t, ...p } : t))); setDirty(true); };
  const themTimer = () => {
    const trong = ds.find((t) => t.nhom === NHOM_KHONG_DUNG);
    if (!trong) { toast.warning('Đã dùng hết 32 timer'); return; }
    doiTimer(trong.so, { nhom: 0, gio_phut: 18 * 60, muc: 100, thu: 0x7f });
  };
  const hien = hienDu ? ds : ds.filter((t) => t.nhom !== NHOM_KHONG_DUNG);
  const soTuTuyen = routeId ? (tree.data ?? []).find((r) => r.id === routeId)?.cabinets.length ?? null : null;
  const guiDi = () => {
    if (che === 'tu') {
      gui.mutate(ds, {
        onSuccess: () => { setDirty(false); setKetTuyen(null); toast.success('Đã gửi lịch dim xuống tủ'); },
        onError: (e) => toast.error('Không gửi được lịch dim', errorMessage(e)),
      });
    } else if (routeId) {
      apTuyen.mutate({ routeId, timers: ds }, {
        onSuccess: (r) => { setDirty(false); setKetTuyen(r.tu); },
        onError: (e) => toast.error('Không áp được cho tuyến', errorMessage(e)),
      });
    }
  };
  const cuoi = q.data!.guiCuoi;

  return (
    <div className="space-y-4" data-o="tab-do-sang">
      {bayGio && bayGio.length > 0 && (
        <p className="m-0 text-sm text-chu-1" data-o="bay-gio"><span className="font-semibold">Bây giờ</span> · {bayGio.join(' · ')}</p>
      )}
      {(q.data!.khac || dirty) && <p className="m-0 text-[12.5px] text-canh" data-o="chua-day">Lịch dim mới chưa đẩy xuống tủ</p>}

      <section className="rounded-xl border border-vien bg-nen p-3.5">
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {THU.map((n, i) => (
            <button key={n} type="button" aria-pressed={i === thu} onClick={() => setThu(i)} data-chip-ngay={i}
              className={`h-8 min-w-[40px] rounded-lg border px-2 text-sm font-semibold ${i === thu ? 'border-nhan bg-nhan/15 text-chu-1' : 'border-vien text-chu-2'}`}>
              {n}{i === homNay.thu ? ' •' : ''}
            </button>
          ))}
          <span className="ml-auto flex flex-wrap gap-x-3 gap-y-1 text-xs text-chu-2">
            {oo.loai === 'co' && nhomDung.map((so) => (
              <span key={so} className="inline-flex items-center gap-1.5" data-chu-giai={so}><span className="h-2.5 w-2.5 rounded-full" style={{ background: mauNhomSo(so) }} />{tenNhomChuGiai(nhomMap.get(so), so)}</span>
            ))}
          </span>
        </div>
        <DoThi ds={ds} thu={thu} nhomMap={nhomMap} oo={oo} bayGio={bayGioTruc} sua={sua} doiTimer={doiTimer} />
      </section>

      <section className="overflow-hidden rounded-xl border border-vien bg-nen">
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
          <h3 className="m-0 text-sm font-semibold text-chu-1">Timer dim</h3>
          <span className="text-xs text-chu-3">{ds.filter((t) => t.nhom !== NHOM_KHONG_DUNG).length}/32</span>
          <span className="ml-auto flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setHienDu((v) => !v)}>{hienDu ? 'Thu gọn' : 'Hiện đủ 32'}</Button>
            {sua && <Button size="sm" variant="secondary" icon={<Plus className="h-4 w-4" />} onClick={themTimer}>Thêm timer</Button>}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-chu-3"><th className="px-3 py-2 font-semibold">Số</th><th className="px-2 py-2 font-semibold">Dùng</th><th className="px-2 py-2 font-semibold">Giờ</th><th className="px-2 py-2 font-semibold">%</th><th className="px-2 py-2 font-semibold">Ngày</th><th className="px-2 py-2 font-semibold">Nhóm</th></tr></thead>
            <tbody>
              {hien.map((t) => <DongTimer key={t.so} t={t} nhom={nhom} sua={sua} doi={(p) => doiTimer(t.so, p)} baoLoi={baoLoi} />)}
              {hien.length === 0 && <tr><td colSpan={6} className="px-4 py-5 text-center text-sm text-chu-3">Chưa dùng timer nào</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <KhoiApCho che={che} doiChe={setChe} onGui={guiDi} dangGui={gui.isPending || apTuyen.isPending} qd={qd} soTuTuyen={soTuTuyen} tenTu={tenTu} khoaGui={loiNhom.size > 0} />
      {cuoi && !ketTuyen && <p className="m-0 text-sm text-chu-2" data-o="trang-thai-gui">Lần gửi gần nhất: <Badge tone={NHAN_TRANG_THAI_LENH[cuoi.state].tone}>{NHAN_TRANG_THAI_LENH[cuoi.state].nhan}</Badge></p>}
      {ketTuyen && (
        <ul className="m-0 list-none space-y-1 p-0 text-sm" data-o="ket-ap-tuyen-dim">
          {ketTuyen.map((k) => (
            <li key={k.cabinetId} className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-chu-1">{k.name}</span>
              <Badge tone={NHAN_TRANG_THAI_LENH[k.state].tone}>{NHAN_TRANG_THAI_LENH[k.state].nhan}</Badge>
              {k.thieuNhom.map((n) => <span key={n} className="text-canh">Tủ {k.name} không có đèn nhóm {n}</span>)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
